/**
 * AppLoggerService — Unified, single-flag performance logger.
 *
 * HOW TO ENABLE (no rebuild required):
 *   localStorage.setItem('APP_LOGS', 'true');  location.reload();
 *
 * HOW TO DISABLE:
 *   localStorage.setItem('APP_LOGS', 'false'); location.reload();
 *
 * SUB-FLAGS (all default ON when master flag is ON):
 *   localStorage.setItem('APP_LOGS_PERF', 'false');  // disable Performance Metrics
 *   localStorage.setItem('APP_LOGS_API',  'false');  // disable API logs
 *   localStorage.setItem('APP_LOGS_HTML', 'false');  // disable HTML/component logs
 *
 * ZERO OVERHEAD when disabled: every public method returns immediately
 * before any string formatting or console.* calls.
 */

import { Injectable, Inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------
type Rating = 'good' | 'needs-improvement' | 'poor';

interface MetricThreshold {
  good: number;
  poor: number;
}

// ---------------------------------------------------------------------------
// Thresholds (ms unless noted)
// ---------------------------------------------------------------------------
const THRESHOLDS: Record<string, MetricThreshold> = {
  FCP:  { good: 1800,  poor: 3000  },
  LCP:  { good: 2500,  poor: 4000  },
  TBT:  { good: 200,   poor: 600   },
  CLS:  { good: 0.1,   poor: 0.25  }, // unitless score
  FID:  { good: 100,   poor: 300   },
  INP:  { good: 200,   poor: 500   },
  TTFB: { good: 800,   poor: 1800  },
  API:  { good: 500,   poor: 1000  },
};

// Improvement tips shown alongside poor / needs-improvement ratings
const TIPS: Record<string, string> = {
  FCP:  '💡 Inline critical CSS, eliminate render-blocking resources, use SSR.',
  LCP:  '💡 Preload hero image (fetchpriority="high"), reduce TTFB, defer off-screen images.',
  TBT:  '💡 Split large JS bundles, defer non-critical scripts, reduce main-thread work.',
  CLS:  '💡 Set explicit width/height on images/videos, avoid late-injected content above the fold.',
  FID:  '💡 Reduce JS parse/execute time, use web workers for heavy tasks.',
  INP:  '💡 Break long tasks with scheduler.postTask or setTimeout(0), avoid synchronous layout.',
  TTFB: '💡 Enable SSR page caching, optimize backend query performance.',
  API:  '💡 Add server-side caching, paginate large responses, use CDN for static data.',
};

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------
@Injectable({ providedIn: 'root' })
export class AppLoggerService implements OnDestroy {
  // Master enabled flag — read once at construction time and refreshed on localStorage change
  private _enabled = false;
  private _perfEnabled  = false;
  private _apiEnabled   = false;
  private _htmlEnabled  = false;

  /** Exposed for interceptors / guards that need a quick check */
  get isEnabled(): boolean  { return this._enabled; }
  get isPerfEnabled(): boolean { return this._perfEnabled; }
  get isApiEnabled(): boolean  { return this._apiEnabled; }
  get isHtmlEnabled(): boolean { return this._htmlEnabled; }

  private readonly isBrowser: boolean;
  private metricsLogged = new Set<string>();

  // Long-task TBT accumulator
  private tbtMs = 0;
  private tbtObserver: PerformanceObserver | null = null;

  // CLS accumulator
  private clsValue = 0;
  private clsObserver: PerformanceObserver | null = null;

  // Observers we keep references to so we can disconnect on destroy
  private observers: PerformanceObserver[] = [];

  // Page navigation start mark
  private navStartTime = 0;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      this._readFlags();
    }
  }

  ngOnDestroy(): void {
    this._disconnectObservers();
  }

  // ---------------------------------------------------------------------------
  // Flag helpers
  // ---------------------------------------------------------------------------

  /**
   * Read flags from window.__APP_LOGS* — set by assets/logging.config.js.
   * That file is a plain static asset: edit it and push/copy to the server.
   * No Angular build, no SSR build, no Node restart required.
   */
  private _readFlags(): void {
    const w = window as any;
    this._enabled     = w.__APP_LOGS       === true;
    this._perfEnabled = this._enabled && w.__APP_LOGS_PERF !== false;
    this._apiEnabled  = this._enabled && w.__APP_LOGS_API  !== false;
    this._htmlEnabled = this._enabled && w.__APP_LOGS_HTML !== false;
  }

  // ---------------------------------------------------------------------------
  // Public: Performance Metrics (Web Vitals + TBT)
  // ---------------------------------------------------------------------------

  /**
   * Initialize all performance metric observers.
   * Call once from AppComponent.ngOnInit (browser-only).
   */
  initPerfMetrics(): void {
    if (!this.isBrowser || !this._perfEnabled) return;

    this.navStartTime = performance.now();

    // Print a banner so it's easy to spot in the console
    console.groupCollapsed(
      '%c📊 Performance Metrics — loading…',
      'color:#7C3AED; font-weight:bold; font-size:13px;'
    );
    console.log('Metrics will appear here as they become available.');
    console.log('Toggle: localStorage.setItem(\'APP_LOGS\', \'true/false\'); location.reload()');
    console.groupEnd();

    this._observeFCP();
    this._observeLCP();
    this._observeTBT();
    this._observeCLS();
    this._observeFID();
    this._observeTTFB();
    this._checkSSRCache();
  }

  // ---------------------------------------------------------------------------
  // Public: API Logs
  // ---------------------------------------------------------------------------

  /**
   * Log an HTTP API call result.
   * Called by PerformanceInterceptor — only runs when _apiEnabled is true.
   */
  logApi(method: string, url: string, durationMs: number, status: string): void {
    if (!this._apiEnabled) return;

    const apiName = this._extractApiName(url);
    const rating = this._getRating(durationMs, THRESHOLDS['API'].good, THRESHOLDS['API'].poor);
    const color = this._ratingColor(rating);
    const icon = rating === 'good' ? '✅' : rating === 'needs-improvement' ? '⚠️' : '🔴';

    console.log(
      `%c[API] ${icon} ${method} ${apiName} — ${durationMs}ms (${status})`,
      `color:${color}; font-weight:bold;`
    );

    if (rating !== 'good') {
      console.groupCollapsed(`  ↳ ${TIPS['API']}`);
      console.log('  Method :', method);
      console.log('  URL    :', url);
      console.log('  Time   :', `${durationMs}ms   (target <${THRESHOLDS['API'].good}ms)`);
      console.log('  Status :', status);
      console.groupEnd();
    }
  }

  // ---------------------------------------------------------------------------
  // Public: HTML / Component Logs
  // ---------------------------------------------------------------------------

  /**
   * Log how long a component took to initialise.
   * Pass `startTime = performance.now()` from the component constructor.
   */
  logComponentLoad(componentName: string, startTime: number): void {
    if (!this._htmlEnabled) return;

    const duration = Math.round(performance.now() - startTime);
    const color = duration < 16 ? '#16A34A' : duration < 50 ? '#D97706' : '#DC2626';
    console.log(
      `%c[HTML] 🧩 Component: ${componentName} loaded in ${duration}ms`,
      `color:${color}; font-weight:bold;`
    );
  }

  /**
   * Log total DOM ready time for the current page.
   * Optionally accepts ssrPreRenderMs if known (passed from SSR timing data).
   */
  logHtmlRendered(page: string, ssrPreRenderMs?: number): void {
    if (!this._htmlEnabled) return;

    const domReady = Math.round(
      performance.getEntriesByType('navigation').length > 0
        ? (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming).domContentLoadedEventEnd
        : performance.now() - this.navStartTime
    );

    console.groupCollapsed(
      `%c[HTML] 📄 Page: ${page} — rendered`,
      'color:#0369A1; font-weight:bold;'
    );
    console.log(`DOM Ready     : ${domReady}ms`);
    if (ssrPreRenderMs !== undefined) {
      console.log(`SSR Pre-render: ${ssrPreRenderMs}ms (server-side)`);
    }
    console.groupEnd();
  }

  /**
   * Log a named server pre-execution phase duration.
   * Call from SSR-side code where you measure Node.js execution.
   * (In the browser, this surfaces timing injected via a meta tag.)
   */
  logServerPreExecution(label: string, durationMs: number): void {
    if (!this._htmlEnabled) return;
    console.log(
      `%c[HTML] 🖥️  SSR: ${label} — ${durationMs}ms`,
      'color:#0891B2; font-weight:bold;'
    );
  }

  /**
   * Read SSR timing injected into the HTML by server.ts and log it.
   * server.ts injects: <meta name="ssr-timing" content="render=340,config=18">
   */
  logSsrTimingFromMeta(): void {
    if (!this._htmlEnabled || !this.isBrowser) return;
    const meta = document.querySelector('meta[name="ssr-timing"]') as HTMLMetaElement | null;
    if (!meta) return;
    const content = meta.getAttribute('content') || '';
    const pairs = content.split(',');
    console.groupCollapsed(
      '%c[HTML] 🖥️  SSR Pre-execution Timing',
      'color:#0891B2; font-weight:bold;'
    );
    pairs.forEach(pair => {
      const [key, val] = pair.split('=');
      if (key && val) {
        console.log(`  ${key.trim()}: ${val.trim()}ms`);
      }
    });
    console.groupEnd();
  }

  // ---------------------------------------------------------------------------
  // Private: Web Vitals observers
  // ---------------------------------------------------------------------------

  private _observeFCP(): void {
    if (!('PerformanceObserver' in window)) return;
    try {
      const obs = new PerformanceObserver((list) => {
        list.getEntries().forEach(entry => {
          if (entry.name === 'first-contentful-paint' && !this.metricsLogged.has('FCP')) {
            this._logMetric('FCP', entry.startTime, THRESHOLDS['FCP'], 'ms');
            this.metricsLogged.add('FCP');
          }
        });
        obs.disconnect();
      });
      obs.observe({ type: 'paint', buffered: true });
      this.observers.push(obs);
    } catch { /* not supported */ }
  }

  private _observeLCP(): void {
    if (!('PerformanceObserver' in window)) return;
    try {
      const obs = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const last = entries[entries.length - 1] as any;
        if (last && !this.metricsLogged.has('LCP')) {
          const lcp = last.renderTime || last.loadTime;
          this._logMetric('LCP', lcp, THRESHOLDS['LCP'], 'ms');
          this.metricsLogged.add('LCP');
          obs.disconnect();
        }
      });
      obs.observe({ type: 'largest-contentful-paint', buffered: true });
      this.observers.push(obs);
    } catch { /* not supported */ }
  }

  private _observeTBT(): void {
    // TBT = sum of (longtask duration - 50ms) for tasks blocking main thread during load
    if (!('PerformanceObserver' in window)) return;
    try {
      this.tbtObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry: any) => {
          if (entry.duration > 50) {
            this.tbtMs += entry.duration - 50;
          }
        });
      });
      this.tbtObserver.observe({ type: 'longtask', buffered: true });
      this.observers.push(this.tbtObserver);

      // Report TBT 3s after load (most blocking occurs during initial load)
      const reportTBT = () => {
        setTimeout(() => {
          if (!this.metricsLogged.has('TBT')) {
            this._logMetric('TBT', Math.round(this.tbtMs), THRESHOLDS['TBT'], 'ms');
            this.metricsLogged.add('TBT');
          }
        }, 3000);
      };

      if (document.readyState === 'complete') {
        reportTBT();
      } else {
        window.addEventListener('load', reportTBT, { once: true });
      }
    } catch { /* longtask not supported in all browsers */ }
  }

  private _observeCLS(): void {
    if (!('PerformanceObserver' in window)) return;
    try {
      this.clsObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry: any) => {
          if (!entry.hadRecentInput) {
            this.clsValue += entry.value;
          }
        });
      });
      this.clsObserver.observe({ type: 'layout-shift', buffered: true });
      this.observers.push(this.clsObserver);

      const reportCLS = () => {
        setTimeout(() => {
          if (!this.metricsLogged.has('CLS')) {
            this._logMetric('CLS', this.clsValue, THRESHOLDS['CLS'], '');
            this.metricsLogged.add('CLS');
          }
        }, 2000);
      };

      if (document.readyState === 'complete') {
        reportCLS();
      } else {
        window.addEventListener('load', reportCLS, { once: true });
      }
    } catch { /* not supported */ }
  }

  private _observeFID(): void {
    if (!('PerformanceObserver' in window)) return;
    try {
      const obs = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry: any) => {
          if (entry.name === 'first-input' && !this.metricsLogged.has('FID')) {
            const fid = entry.processingStart - entry.startTime;
            this._logMetric('FID', fid, THRESHOLDS['FID'], 'ms');
            this.metricsLogged.add('FID');
          }
        });
        obs.disconnect();
      });
      obs.observe({ type: 'first-input', buffered: true });
      this.observers.push(obs);
    } catch { /* not supported */ }
  }

  private _observeTTFB(): void {
    try {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (nav && !this.metricsLogged.has('TTFB')) {
        const ttfb = nav.responseStart - nav.requestStart;
        this._logMetric('TTFB', ttfb, THRESHOLDS['TTFB'], 'ms');
        this.metricsLogged.add('TTFB');
      }
    } catch { /* not supported */ }
  }

  private _checkSSRCache(): void {
    try {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (nav) {
        const ttfb = nav.responseStart - nav.requestStart;
        const fromCache = ttfb < 50;
        console.log(
          `%c[SSR] ${fromCache ? '⚡ Served from CACHE' : '🔄 Fresh render'} (TTFB: ${Math.round(ttfb)}ms)`,
          `color:${fromCache ? '#16A34A' : '#2563EB'}; font-weight:bold;`
        );
      }
    } catch { /* not supported */ }
  }

  // ---------------------------------------------------------------------------
  // Private: logging helpers
  // ---------------------------------------------------------------------------

  private _logMetric(name: string, value: number, threshold: MetricThreshold, unit: string): void {
    if (!this._perfEnabled) return;

    const rating = this._getRating(value, threshold.good, threshold.poor);
    const color  = this._ratingColor(rating);
    const icon   = rating === 'good' ? '✅' : rating === 'needs-improvement' ? '⚠️' : '🔴';
    const displayValue = unit === '' ? value.toFixed(3) : `${Math.round(value)}${unit}`;
    const targetLabel = `target <${threshold.good}${unit === 'ms' ? 'ms' : unit === '' ? '' : unit}`;

    console.log(
      `%c[PERF] ${icon} ${name.padEnd(4)} ${this._metricLabel(name)}: ${displayValue}   (${rating.toUpperCase()} — ${targetLabel})`,
      `color:${color}; font-weight:bold;`
    );

    // Show improvement tip when not 'good'
    if (rating !== 'good' && TIPS[name]) {
      console.log(`%c       ${TIPS[name]}`, 'color:#6B7280; font-style:italic; font-size:11px;');
    }
  }

  private _metricLabel(name: string): string {
    const labels: Record<string, string> = {
      FCP:  'First Contentful Paint   ',
      LCP:  'Largest Contentful Paint ',
      TBT:  'Total Blocking Time      ',
      CLS:  'Cumulative Layout Shift  ',
      FID:  'First Input Delay        ',
      INP:  'Interaction to Next Paint',
      TTFB: 'Time to First Byte       ',
    };
    return labels[name] || name;
  }

  private _getRating(value: number, good: number, poor: number): Rating {
    if (value <= good) return 'good';
    if (value <= poor) return 'needs-improvement';
    return 'poor';
  }

  private _ratingColor(rating: Rating): string {
    return rating === 'good' ? '#16A34A' : rating === 'needs-improvement' ? '#D97706' : '#DC2626';
  }

  private _extractApiName(url: string): string {
    try {
      const urlObj = new URL(url);
      const parts = urlObj.pathname.split('/').filter(p => p);
      return parts.slice(-3).join('/') || urlObj.pathname;
    } catch {
      const parts = url.split('/').filter(p => p && !p.startsWith('http'));
      return parts.slice(-3).join('/') || url;
    }
  }

  private _disconnectObservers(): void {
    this.observers.forEach(obs => { try { obs.disconnect(); } catch { /* ignore */ } });
    this.observers = [];
  }
}
